type UnknownRecord = Record<string, unknown>;

const normalizeType = (dataType?: string | null): string =>
  (dataType ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

const isPlainObject = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date);

const canonicalDecimal = (value: unknown): string | null => {
  if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'bigint') return null;
  const text = String(value).trim();
  const match = /^([+-]?)(\d+)(?:\.(\d*))?(?:e([+-]?\d+))?$/i.exec(text);
  if (!match) return null;

  const negative = match[1] === '-';
  const fraction = match[3] ?? '';
  const exponent = Number(match[4] ?? 0);
  if (!Number.isSafeInteger(exponent)) return null;

  let digits = `${match[2]}${fraction}`.replace(/^0+/, '') || '0';
  let scale = fraction.length - exponent;
  while (scale > 0 && digits.endsWith('0')) {
    digits = digits.slice(0, -1) || '0';
    scale -= 1;
  }

  let canonical: string;
  if (digits === '0') {
    canonical = '0';
  } else if (scale <= 0) {
    canonical = `${digits}${'0'.repeat(-scale)}`;
  } else if (digits.length <= scale) {
    canonical = `0.${'0'.repeat(scale - digits.length)}${digits}`;
  } else {
    canonical = `${digits.slice(0, digits.length - scale)}.${digits.slice(digits.length - scale)}`;
  }
  return negative && canonical !== '0' ? `-${canonical}` : canonical;
};

const canonicalDate = (value: unknown): string | null => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  if (typeof value !== 'string') return null;
  const match = /^(\d{4}-\d{2}-\d{2})(?:[T\s].*)?$/.exec(value.trim());
  return match?.[1] ?? null;
};

const canonicalInstant = (value: unknown): string | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : `${BigInt(value.getTime()) * 1_000_000n}`;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? `${BigInt(Math.trunc(value)) * 1_000_000n}` : null;
  }
  if (typeof value !== 'string') return null;

  const text = value.trim().replace(/([+-]\d{2})$/, '$1:00');
  const match = /^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2}):(\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:?\d{2})$/i.exec(text);
  if (!match) return null;
  const wholeSecond = Date.parse(`${match[1]}T${match[2]}:${match[3]}${match[5]}`);
  if (Number.isNaN(wholeSecond)) return null;
  const nanoseconds = BigInt((match[4] ?? '').slice(0, 9).padEnd(9, '0'));
  return `${BigInt(wholeSecond) * 1_000_000n + nanoseconds}`;
};

const canonicalLocalTimestamp = (value: unknown): string | null => {
  const text = value instanceof Date
    ? (Number.isNaN(value.getTime()) ? '' : value.toISOString().replace(/Z$/, ''))
    : typeof value === 'string' ? value.trim() : '';
  const match = /^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})(?::(\d{2}))?(?:\.(\d+))?$/.exec(text);
  if (!match) return null;
  const fraction = (match[4] ?? '').replace(/0+$/, '');
  return `${match[1]}T${match[2]}:${match[3] ?? '00'}${fraction ? `.${fraction}` : ''}`;
};

const canonicalLocalTime = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?$/.exec(value.trim());
  if (!match) return null;
  const fraction = (match[4] ?? '').replace(/0+$/, '');
  return `${match[1]}:${match[2]}:${match[3] ?? '00'}${fraction ? `.${fraction}` : ''}`;
};

const canonicalZonedTime = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const text = value.trim().replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  return canonicalInstant(`2000-01-01T${text}`);
};

const canonicalBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const text = String(value).trim().toLowerCase();
  if (['true', 't', '1'].includes(text)) return true;
  if (['false', 'f', '0'].includes(text)) return false;
  return null;
};

const parseJsonValue = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const structuralValuesAreEqual = (current: unknown, expected: unknown): boolean => {
  if (Object.is(current, expected)) return true;
  if (current == null || expected == null) return current == null && expected == null;
  if (current instanceof Date || expected instanceof Date) {
    const currentInstant = canonicalInstant(current);
    const expectedInstant = canonicalInstant(expected);
    return currentInstant !== null && expectedInstant !== null && currentInstant === expectedInstant;
  }
  if (Array.isArray(current) || Array.isArray(expected)) {
    return Array.isArray(current)
      && Array.isArray(expected)
      && current.length === expected.length
      && current.every((value, index) => structuralValuesAreEqual(value, expected[index]));
  }
  if (isPlainObject(current) || isPlainObject(expected)) {
    if (!isPlainObject(current) || !isPlainObject(expected)) return false;
    const currentKeys = Object.keys(current).sort();
    const expectedKeys = Object.keys(expected).sort();
    return currentKeys.length === expectedKeys.length
      && currentKeys.every((key, index) => key === expectedKeys[index]
        && structuralValuesAreEqual(current[key], expected[key]));
  }

  return false;
};

/**
 * Compare a value returned by PostgREST with the value the edit originally loaded.
 * PostgreSQL and form controls represent several equal values differently; this
 * comparison is deliberately type-aware so those wire-format differences trigger
 * the safe retry path without hiding a genuine same-column change.
 */
export const databaseValuesAreEqual = (
  current: unknown,
  expected: unknown,
  dataType?: string | null,
): boolean => {
  if (Object.is(current, expected)) return true;
  if (current == null || expected == null) return current == null && expected == null;

  const type = normalizeType(dataType);
  if (['smallint', 'integer', 'bigint', 'decimal', 'numeric', 'real', 'double precision', 'float4', 'float8'].includes(type)) {
    const currentNumber = canonicalDecimal(current);
    const expectedNumber = canonicalDecimal(expected);
    return currentNumber !== null && expectedNumber !== null && currentNumber === expectedNumber;
  }
  if (type === 'boolean' || type === 'bool') {
    const currentBoolean = canonicalBoolean(current);
    const expectedBoolean = canonicalBoolean(expected);
    return currentBoolean !== null && expectedBoolean !== null && currentBoolean === expectedBoolean;
  }
  if (type === 'date') {
    const currentDate = canonicalDate(current);
    const expectedDate = canonicalDate(expected);
    return currentDate !== null && expectedDate !== null && currentDate === expectedDate;
  }
  if (type === 'timestamp with time zone' || type === 'timestamptz') {
    const currentInstant = canonicalInstant(current);
    const expectedInstant = canonicalInstant(expected);
    return currentInstant !== null && expectedInstant !== null && currentInstant === expectedInstant;
  }
  if (type === 'timestamp' || type === 'timestamp without time zone') {
    const currentTimestamp = canonicalLocalTimestamp(current);
    const expectedTimestamp = canonicalLocalTimestamp(expected);
    return currentTimestamp !== null && expectedTimestamp !== null && currentTimestamp === expectedTimestamp;
  }
  if (type === 'time with time zone' || type === 'timetz') {
    const currentTime = canonicalZonedTime(current);
    const expectedTime = canonicalZonedTime(expected);
    return currentTime !== null && expectedTime !== null && currentTime === expectedTime;
  }
  if (type === 'time' || type === 'time without time zone') {
    const currentTime = canonicalLocalTime(current);
    const expectedTime = canonicalLocalTime(expected);
    return currentTime !== null && expectedTime !== null && currentTime === expectedTime;
  }
  if (type === 'uuid') {
    return typeof current === 'string'
      && typeof expected === 'string'
      && current.toLowerCase() === expected.toLowerCase();
  }
  if (type === 'json' || type === 'jsonb') {
    return structuralValuesAreEqual(parseJsonValue(current), parseJsonValue(expected));
  }
  if (type.endsWith('[]')) {
    if (!Array.isArray(current) || !Array.isArray(expected) || current.length !== expected.length) return false;
    const elementType = type.slice(0, -2);
    return current.every((value, index) => databaseValuesAreEqual(value, expected[index], elementType));
  }

  if (structuralValuesAreEqual(current, expected)) return true;
  const currentNumber = canonicalDecimal(current);
  const expectedNumber = canonicalDecimal(expected);
  if ((typeof current === 'number' || typeof current === 'bigint')
    !== (typeof expected === 'number' || typeof expected === 'bigint')) {
    return currentNumber !== null && expectedNumber !== null && currentNumber === expectedNumber;
  }
  return false;
};

export const normalizeStaleWriteFilterValue = (value: unknown, dataType?: string | null): unknown => {
  if (!(value instanceof Date)) return value;
  const type = normalizeType(dataType);
  if (type === 'date') return canonicalDate(value);
  if (type === 'timestamp' || type === 'timestamp without time zone') return canonicalLocalTimestamp(value)?.replace('T', ' ');
  return Number.isNaN(value.getTime()) ? value : value.toISOString();
};
